import { gsap } from './gsap'

type Cleanup = (() => void) | void

/**
 * Scroll-scrubbed sections (Webflow SCROLLING_IN_VIEW lists a-29 / a-30 / a-55).
 * The scroll position drives the playhead, so these map cleanly to ScrollTrigger
 * with `scrub`.
 */
export const setupScrubbed = (root: HTMLElement): Cleanup => {
    // a-29 — Dark/Light mode switch: the white "light mode" panel wipes up 0 -> 100vh.
    // The wipe must be driven by the TALL 250vh track (`.dark-and-light-mode-holder`),
    // not the inner 100vh `position: sticky` container. The sticky element stays pinned
    // to the viewport, so its top/bottom resolve to the same scroll position and the
    // wipe would snap at one spot. Triggering off the holder maps the wipe across the
    // whole pinned scroll: it begins the instant the section sticks (holder top hits the
    // viewport top) and finishes as it unpins (holder bottom hits the viewport bottom) —
    // matching Webflow's scroll-in-view behaviour.
    const dlHolder = root.querySelector<HTMLElement>('.dark-and-light-mode-holder')
    const whiteHolder = root.querySelector<HTMLElement>('.white-mode-holder')

    if (dlHolder && whiteHolder) {
        gsap.fromTo(
            whiteHolder,
            { height: '0vh' },
            {
                height: '100vh',
                ease: 'none',
                scrollTrigger: {
                    trigger: dlHolder,
                    start: 'top top',
                    end: 'bottom bottom',
                    scrub: 0.5,
                    // Recompute the 0vh/100vh endpoints (and the start/end scroll
                    // positions) for the CURRENT viewport on every refresh/resize.
                    // Without this GSAP bakes the vh values into pixels at creation
                    // time, so on a phone (or after a rotate / browser-chrome resize)
                    // the wipe is mapped to stale desktop numbers and the white panel
                    // shows at the wrong scroll point. This keeps the exact same logic
                    // correctly scaled to every device.
                    invalidateOnRefresh: true
                }
            }
        )
    }

    // a-30 — Integration section: iphone rises, light blooms, divider line draws.
    const integration = root.querySelector<HTMLElement>('.effortlessly-integration-section')

    if (integration) {
        const iphone = integration.querySelector<HTMLElement>('.iphone-holder-effortlessly-integration')
        const line = integration.querySelector<HTMLElement>('.line')
        const light = integration.querySelector<HTMLElement>('.light-holder')

        const tl = gsap.timeline({
            scrollTrigger: { trigger: integration, start: 'top bottom', end: 'bottom top', scrub: 0.92, invalidateOnRefresh: true }
        })

        if (iphone) {
            gsap.set(iphone, { y: 200 })
            tl.to(iphone, { y: 0, ease: 'none', duration: 0.35 }, 0.2)
        }

        if (line) {
            gsap.set(line, { width: '0%' })
            tl.to(line, { width: '80%', ease: 'none', duration: 0.35 }, 0.2)
        }

        if (light) {
            // Keep the blur radius fixed (CSS) and bloom via opacity only (GPU-cheap)
            // rather than animating filter: blur(), which janks Safari on scroll.
            gsap.set(light, { autoAlpha: 0 })
            tl.to(light, { autoAlpha: 0.23, ease: 'none', duration: 0.1 }, 0.48)
        }

        tl.to({}, { duration: 0.01 }, 1)
    }

    // a-55 — About CTA gradient fade in over scroll.
    const ctaHolder = root.querySelector<HTMLElement>('.open-roles-cta-holder')
    const ctaGradient = ctaHolder?.querySelector<HTMLElement>('.cta-gradient')

    if (ctaHolder && ctaGradient) {
        gsap.fromTo(
            ctaGradient,
            { autoAlpha: 0 },
            {
                autoAlpha: 1,
                ease: 'none',
                scrollTrigger: { trigger: ctaHolder, start: 'top bottom', end: 'center center', scrub: 0.92, invalidateOnRefresh: true }
            }
        )
    }
}
